import { useQuery } from "react-query";
import { sureApiCall } from "src/api/axiosClient/sureApi";
import { z } from "zod";
import { zodParseWithLog } from "src/api/apiErrorLogs";
import { FieldSchema, UnitSchema } from "../ssq/useGetOneSsqVersion";
import { DataListOptionSchema } from "src/api/sure-api/dataList/useGetAllDataList";

export const getOneActionKey = "getOneAction" as const;

export type Options = {
    actionId: string | undefined;
};
export const useGetOneAction = (options: Options) => {
    const { actionId } = options;

    const query = useQuery({
        queryKey: [getOneActionKey, actionId],
        cacheTime: 0,
        enabled: actionId !== undefined,
        queryFn: async () => {
            const url = `/api/actions/${actionId}`;
            return await sureApiCall.get(url);
        },
        select: (response) => {
            const action = response.data;
            const data = zodParseWithLog(action, ActionsSchema, getOneActionKey);
            return data;
        },
    });

    return {
        action: query.data,
        ...query,
    };
};

export type DataListOption = z.infer<typeof DataListOptionSchema>;

const DataListSchema = z.object({
    options: DataListOptionSchema.array(),
});

export type SsqPropertyAction = z.infer<typeof SsqPropertiesSchema>;
const SsqPropertiesSchemaHead = z.object({
    id: z.number(),
    code: z.string().optional(),
    title: z.string().optional(),
    name: z.string().optional(),
    field: FieldSchema.optional(),
    unit: UnitSchema.optional(),
    precision: z.number().optional(),
    position: z.number().optional(),
    multivalue: z.boolean().optional(),
    dataList: DataListSchema.optional(),
    defaultValue: z.string().optional(),
    hidden: z.boolean().optional(),
    hint: z.string().optional(),
    readonly: z.boolean().optional(),
    genericValue: z.string().optional(),
    visibleInActionParametrization: z.boolean().optional(),
    // Additional Properties field only for action call
    actionChildrenIds: z.number().array().optional(),
});
export const SsqPropertiesSchema = SsqPropertiesSchemaHead.extend({
    // The optional here is to handle a level of nested.
    children: SsqPropertiesSchemaHead.array().optional(),
    generalizations: SsqPropertiesSchemaHead.array().optional(),
});
export type SsqProperty = z.infer<typeof SsqPropertiesSchema>;

const CsrThemeParentSchema = z.object({
    id: z.number(),
    title: z.string(),
    iconName: z.string().optional(),
    position: z.number().nullable().optional(),
});
export type CsrThemeParent = z.infer<typeof CsrThemeParentSchema>;
const SureCsrThemeSchema = z.object({
    "@id": z.string(),
    id: z.number(),
    parent: CsrThemeParentSchema.optional(),
    iconName: z.string().optional(),
    slug: z.string(),
    title: z.string(),
    description: z.string().optional(),
    position: z.number().nullable().optional(),
});
export type SureCsrTheme = z.infer<typeof SureCsrThemeSchema>;

export const ActionFieldsSchema = z.object({
    id: z.number(),
});
export type ActionFields = z.infer<typeof ActionFieldsSchema>;

export const ActionsSchema = z.object({
    id: z.number(),
    title: z.string(),
    slug: z.string(),
    description: z.string().optional(),
    imageUrl: z.string().url().nullish(),
    position: z.number().nullable().optional(),
    actionFields: ActionFieldsSchema.array(),
    csrThemes: SureCsrThemeSchema.array(),
    ssqProperties: SsqPropertiesSchema.array(),
});
export type ActionsType = z.infer<typeof ActionsSchema>;

export const ActionsListSchema = ActionsSchema.omit({
    actionFields: true,
    ssqProperties: true,
}).extend({
    actionFields: z.string().array(),
    ssqPropertyIds: z.number().array().optional(),
});
export type ActionsListType = z.infer<typeof ActionsListSchema>;
